import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AlertCircle, ArrowLeft, FileText } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { DisabledHint } from '@/components/ui/disabled-hint'

import type {
  LineResponseDto,
  QuoteDraftVersion,
  QuoteLineOrigin,
  QuoteVariant,
  QuoteV2,
} from '@/components/features/project-detail/quotes/types'
import {
  deleteQuoteVersionLineV2,
  getProjectQuotesV2,
  getQuoteDraftDetailsV2,
  getQuoteVersionLineV2,
  moveQuoteVersionLineV2,
} from '@/components/features/project-detail/quotes/quotes-v2.api'

import { SelectSalesItemDialog } from '@/components/features/project-detail/items/add-item/SelectSalesItemDialog'
import type { FamilyDto } from '@/components/features/project-detail/items/add-item/types'
import { FamilyEditorSidebar } from '@/components/features/project-detail/items/editor/FamilyEditorSidebar'

import { DiscountLineDialog } from '@/components/features/project-detail/quotes/DiscountLineDialog'
import { ImportedLineDialog } from '@/components/features/project-detail/quotes/ImportedLineDialog'
import { NoteLineDialog } from '@/components/features/project-detail/quotes/NoteLineDialog'
import { QuoteVersionBaseFinishesDialog } from '@/components/features/project-detail/quotes/QuoteVersionBaseFinishesDialog'

import { ActiveDraftCard } from '@/components/features/project-detail/quotes/ActiveDraftCard'
import { QuoteLineVatMismatchBanner } from '@/components/features/project-detail/quotes/QuoteLineVatMismatchBanner'
import { VatAlignmentDialog } from '@/components/features/project-detail/quotes/VatAlignmentDialog'
import { WinnerSourceFileButton } from '@/components/features/winner-import/WinnerSourceFileButton'
import { LineSupplyDialog } from '@/components/features/supply/LineSupplyDialog'
import { getSupplyOptions, setQuoteSupply, type SupplyOption } from '@/components/features/supply/supply.api'
import {
  clearDismissed,
  isDismissed,
  markDismissed,
} from '@/components/features/project-detail/quotes/vatAlignmentHelpers'
import { useProjectOptions } from '@/hooks/useProjectOptions'
import { MODULES, useModules } from '@/hooks/useModules'

type RouteParams = { id: string; quoteId: string; versionId: string }

export default function QuoteV2DraftEditorPage() {
  const navigate = useNavigate()
  const { id: projectId, quoteId, versionId } = useParams<RouteParams>()

  const { options: projectOptions } = useProjectOptions()

  const [quote, setQuote] = useState<QuoteV2 | null>(null)
  const [variant, setVariant] = useState<QuoteVariant | null>(null)
  const [draft, setDraft] = useState<QuoteDraftVersion | null>(null)
  const [draftMetaName, setDraftMetaName] = useState<string>('')
  const [draftMetaVersionNumber, setDraftMetaVersionNumber] = useState<number | null>(null)

  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [isSelectItemOpen, setIsSelectItemOpen] = useState(false)
  const [selectedFamily, setSelectedFamily] = useState<FamilyDto | null>(null)
  const [isFamilyEditorOpen, setIsFamilyEditorOpen] = useState(false)

  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null)
  const [selectedDraftVersionId, setSelectedDraftVersionId] = useState<string | null>(null)

  const [isDiscountDialogOpen, setIsDiscountDialogOpen] = useState(false)
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false)
  const [editingLine, setEditingLine] = useState<LineResponseDto | null>(null)
  const [editingLineId, setEditingLineId] = useState<string | null>(null)
  const [isEditingDiscount, setIsEditingDiscount] = useState(false)
  const [isEditingNote, setIsEditingNote] = useState(false)
  const [isEditingFamilyLine, setIsEditingFamilyLine] = useState(false)
  // SOLY-IMPORT-003 S05: an imported line has no catalogue family; it opens its own dialog.
  const [importedLineOrigin, setImportedLineOrigin] = useState<QuoteLineOrigin | null>(null)
  const [isImportedLineOpen, setIsImportedLineOpen] = useState(false)
  const [showOriginFirst, setShowOriginFirst] = useState(false)

  const [isBaseFinishesOpen, setIsBaseFinishesOpen] = useState(false)

  // SOLY-IMPORT-003 S11: the Appro column and its panel, part of quote_import (S19).
  const { hasModule } = useModules()
  const hasSupply = hasModule(MODULES.QUOTE_IMPORT)
  const [suppliers, setSuppliers] = useState<SupplyOption[]>([])
  const [supplyLineId, setSupplyLineId] = useState<string | null>(null)
  const supplierNames = useMemo(() => new Map(suppliers.map((s) => [s.id, s.name])), [suppliers])
  const supplyLine = draft?.linesPreview.find((l) => l.id === supplyLineId) ?? null

  useEffect(() => {
    if (!projectId || !hasSupply) return
    let active = true
    // Without the list the column still reads; a supplier then shows as « fournisseur supprimé ».
    getSupplyOptions(projectId).then((options) => { if (active) setSuppliers(options) }).catch(() => {})
    return () => { active = false }
  }, [projectId, hasSupply])

  // SOLY-VAT-001: banner + alignment dialog state. `dismissTick` re-evaluates the
  // sessionStorage dismiss flag whenever the user clicks "Ignorer" (state change
  // triggers re-render so the banner disappears without a reload).
  const [isVatAlignDialogOpen, setIsVatAlignDialogOpen] = useState(false)
  const [vatDismissTick, setVatDismissTick] = useState(0)

  const resolvedBackUrl = useMemo(() => {
    if (!projectId) return '/home'
    return `/projects/${projectId}?tab=quotes`
  }, [projectId])

  // SOLY-VAT-001: derived visibility — re-evaluated whenever the draft DTO changes or the
  // user dismisses (vatDismissTick increments). Reading sessionStorage in the memo deps
  // is harmless because the only trigger is the dismiss click.
  const vatBannerVisible = useMemo(() => {
    if (!draft) return false
    if (!draft.vatAlignment.shouldRecommendAlignment) return false
    return !isDismissed(draft.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, vatDismissTick])

  const reload = async () => {
    if (!projectId || !quoteId || !versionId) return

    const response = await getProjectQuotesV2(projectId)
    const foundQuote = (response.quotes || []).find((q) => q.id === quoteId) || null
    const foundVariant = (foundQuote?.variants || [])[0] || null
    const draftMeta = (foundVariant?.draftVersions || []).find((v) => v.id === versionId) || null
    const hasDraft = Boolean(draftMeta)
    const foundDraft = hasDraft ? await getQuoteDraftDetailsV2(projectId, quoteId, versionId) : null

    setQuote(foundQuote)
    setVariant(foundVariant)
    setDraft(foundDraft)
    setDraftMetaName(draftMeta?.name?.trim() || '')
    setDraftMetaVersionNumber(draftMeta?.versionNumber ?? null)
  }

  useEffect(() => {
    if (!projectId || !quoteId || !versionId) {
      navigate(resolvedBackUrl)
      return
    }

    let cancelled = false

    const load = async () => {
      try {
        setIsLoading(true)
        setError(null)

        await reload()
      } catch {
        if (cancelled) return
        setError('Erreur lors du chargement du brouillon')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    load()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, quoteId, versionId, navigate, resolvedBackUrl])

  const handleAddDraftLineForVersion = async () => {
    if (!draft || !projectId) return
    setError(null)
    setSelectedQuoteId(quoteId || null)
    setSelectedDraftVersionId(draft.id)
    setIsSelectItemOpen(true)
  }

  const handleAddDiscountForVersion = async () => {
    if (!draft) return
    setError(null)
    setSelectedQuoteId(quoteId || null)
    setSelectedDraftVersionId(draft.id)
    setEditingLine(null)
    setEditingLineId(null)
    setIsEditingDiscount(false)
    setIsDiscountDialogOpen(true)
  }

  const handleAddNoteForVersion = async () => {
    if (!draft) return
    setError(null)
    setSelectedQuoteId(quoteId || null)
    setSelectedDraftVersionId(draft.id)
    setEditingLine(null)
    setEditingLineId(null)
    setIsEditingNote(false)
    setIsNoteDialogOpen(true)
  }

  const handleEditDraftLineForVersion = async (lineId: string, openOnOrigin = false) => {
    if (!projectId || !draft || !quoteId) return

    try {
      setError(null)
      setSelectedQuoteId(quoteId)
      setSelectedDraftVersionId(draft.id)
      setEditingLine(null)
      setEditingLineId(lineId)
      setIsEditingDiscount(false)
      setIsEditingNote(false)
      setIsEditingFamilyLine(false)

      const line = await getQuoteVersionLineV2(projectId, quoteId, draft.id, lineId)
      setEditingLine(line)

      if (line.kind === 'discount') {
        setIsEditingDiscount(true)
        setIsDiscountDialogOpen(true)
        return
      }

      if (line.kind === 'note') {
        setIsEditingNote(true)
        setIsNoteDialogOpen(true)
        return
      }

      const origin = draft.linesPreview.find((l) => l.id === lineId)?.origin ?? null
      if (origin && !line.familyId) {
        setImportedLineOrigin(origin)
        setShowOriginFirst(openOnOrigin)
        setIsImportedLineOpen(true)
        return
      }

      if (!line.familyId || !line.familyKey) {
        setError("Cette ligne n'est pas éditable via le sidebar")
        setEditingLine(null)
        setEditingLineId(null)
        return
      }

      const familyForSidebar: FamilyDto = {
        familyId: line.familyId || '',
        familyKey: line.familyKey || '',
        familyLabel: line.title,
        icon: '🧩',
        familyGroup: line.familyKey || '',
        familyGroupLabel: line.title,
        familyGroupOrder: 0,
      }

      setSelectedFamily(familyForSidebar)
      setIsEditingFamilyLine(true)
      setIsFamilyEditorOpen(true)
    } catch {
      setError("Erreur lors de l'ouverture de la ligne")
    }
  }

  const handleMoveDraftLineForVersion = async (lineId: string, newDisplayOrder: number) => {
    if (!projectId || !draft || !quoteId) return

    try {
      setError(null)
      await moveQuoteVersionLineV2(projectId, quoteId, draft.id, lineId, newDisplayOrder)
      await reload()
    } catch {
      setError('Erreur lors du déplacement de la ligne')
    }
  }

  const handleDeleteDraftLineForVersion = async (lineId: string) => {
    if (!projectId || !draft || !quoteId) return

    try {
      setError(null)
      await deleteQuoteVersionLineV2(projectId, quoteId, draft.id, lineId)
      await reload()
    } catch {
      setError('Erreur lors de la suppression de la ligne')
    }
  }

  const documentNumber = quote?.documentNumber || variant?.documentNumber || null

  if (isLoading) {
    return (
      <div className="min-h-screen bg-muted/30">
        <div className="sticky top-0 z-50 bg-background border-b">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <Button variant="ghost" size="sm" disabled>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </Button>
            <Skeleton className="h-4 w-40" />
            <div />
          </div>
        </div>
        <div className="container mx-auto px-4 py-6">
          <div className="rounded-lg border bg-background p-4">
            <Skeleton className="h-5 w-56" />
            <Skeleton className="mt-3 h-3 w-80" />
            <Skeleton className="mt-6 h-40 w-full" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !projectId || !quoteId || !draft) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 p-4">
        <div className="flex flex-col items-center gap-4 max-w-md text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-8 w-8 text-destructive" />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold">Brouillon indisponible</h2>
            <p className="text-sm text-muted-foreground">{error || "Impossible de charger ce brouillon"}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => navigate(resolvedBackUrl)} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour aux devis
          </Button>
          <Button onClick={() => window.location.reload()}>Réessayer</Button>
        </div>
      </div>
    )
  }

  const actions = (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => {
          setSelectedQuoteId(quoteId)
          setSelectedDraftVersionId(draft.id)
          setIsBaseFinishesOpen(true)
        }}
      >
        Finitions
      </Button>

      <Button type="button" variant="outline" size="sm" onClick={handleAddNoteForVersion}>
        + Option
      </Button>

      <Button type="button" variant="outline" size="sm" onClick={handleAddNoteForVersion}>
        + Note
      </Button>

      <Button type="button" variant="outline" size="sm" onClick={handleAddDiscountForVersion}>
        - Remise
      </Button>

      <Button
        data-testid="editor-add-line-btn"
        type="button"
        variant="outline"
        size="sm"
        onClick={handleAddDraftLineForVersion}
        disabled={!draft.canAddLine}
      >
        + Ajouter élément
      </Button>
    </>
  )

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="sticky top-0 z-50 bg-background border-b shadow-sm">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button data-testid="editor-back-btn" variant="ghost" size="sm" onClick={() => navigate(resolvedBackUrl)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour aux devis
            </Button>
            <div className="border-l pl-3 min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <h1 className="text-lg font-semibold truncate">
                  {(draftMetaName || draft?.name?.trim() || 'Brouillon')}
                  {draftMetaVersionNumber != null ? ` V${draftMetaVersionNumber}` : ''}
                </h1>
                <Badge variant="outline" className="text-[11px] px-1.5 py-0">
                  Édition
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground truncate">
                {documentNumber ? `Devis: ${documentNumber}` : 'Devis sans numéro'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <WinnerSourceFileButton quoteId={quoteId} />
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedQuoteId(quoteId)
                setSelectedDraftVersionId(draft.id)
                setIsBaseFinishesOpen(true)
              }}
            >
              Finitions
            </Button>
            {/* QA staging 2026-08-20 — the reason was written and UNREADABLE: `title` on a shadcn
                Button carries `disabled:pointer-events-none`, which removes the element from
                hit-testing, so the native tooltip never fires. The hint has to ride the wrapper.
                Same trap as PlanningActivationCard and ReportArea — see DisabledHint. */}
            <DisabledHint hint={!draft.canGenerateVersion ? draft.generateDisabledReason || undefined : undefined}>
              <Button
                data-testid="editor-preview-pdf-btn"
                size="sm"
                className="gap-2"
                onClick={() => navigate(`/projets/${projectId}/devis-v2/${quoteId}/versions/${draft.id}/preview`)}
                disabled={!draft.canGenerateVersion}
              >
                <FileText className="h-4 w-4" />
                Aperçu PDF
              </Button>
            </DisabledHint>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-6xl px-4 py-4 space-y-4 pb-24 lg:pb-4">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_280px]">
          <div className="rounded-lg border bg-background p-4">
            <div className="text-sm font-semibold">Brouillon</div>
            <div className="text-xs text-muted-foreground">Ajoute des lignes, ajustements, puis génère une version PDF.</div>

            {vatBannerVisible && (
              <QuoteLineVatMismatchBanner
                alignment={draft.vatAlignment}
                isAligning={false}
                onOpenDialog={() => setIsVatAlignDialogOpen(true)}
                onDismiss={() => {
                  markDismissed(draft.id)
                  setVatDismissTick((t) => t + 1)
                }}
              />
            )}

            <ActiveDraftCard
              draft={draft}
              onAddLine={handleAddDraftLineForVersion}
              onAddDiscount={handleAddDiscountForVersion}
              onAddNote={handleAddNoteForVersion}
              onEditWorkaroundNote={() => {}}
              onEditLine={(lineId) => handleEditDraftLineForVersion(lineId)}
              onShowLineOrigin={(lineId) => handleEditDraftLineForVersion(lineId, true)}
              onMoveLine={handleMoveDraftLineForVersion}
              onDeleteLine={handleDeleteDraftLineForVersion}
              onEditSupply={hasSupply ? setSupplyLineId : undefined}
              supplierNames={supplierNames}
              onGenerateVersion={() => {
                navigate(`/projets/${projectId}/devis-v2/${quoteId}/versions/${draft.id}/preview`)
              }}
              onEditBaseFinishes={() => {
                setSelectedQuoteId(quoteId)
                setSelectedDraftVersionId(draft.id)
                setIsBaseFinishesOpen(true)
              }}
              showBaseFinishesActions
              showFooterActions={false}
            />

            {projectId && quoteId && (
              <VatAlignmentDialog
                open={isVatAlignDialogOpen}
                onOpenChange={setIsVatAlignDialogOpen}
                projectId={projectId}
                quoteId={quoteId}
                versionId={draft.id}
                lines={draft.linesPreview}
                alignment={draft.vatAlignment}
                onAlignedSuccess={async () => {
                  // Re-arm the banner if a future mismatch arises on this version.
                  clearDismissed(draft.id)
                  setVatDismissTick((t) => t + 1)
                  await reload()
                }}
              />
            )}
          </div>

          <div className="hidden lg:block">
            <div className="sticky top-20 rounded-lg border bg-background p-3">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</div>
              <div className="mt-3 grid gap-2">{actions}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/70 lg:hidden">
        <div className="mx-auto w-full max-w-6xl px-4 py-3">
          <div className="flex flex-wrap items-center justify-end gap-2">{actions}</div>
        </div>
      </div>

      <LineSupplyDialog
        open={supplyLine !== null}
        onOpenChange={(open) => { if (!open) setSupplyLineId(null) }}
        title={supplyLine?.label ?? ''}
        subtitle={supplyLine ? `Vendu : ${supplyLine.quantity ?? '—'}${supplyLine.origin ? ' · importée de Winner' : ''}` : null}
        initial={supplyLine?.supply ?? null}
        suppliers={suppliers}
        soldQuantity={supplyLine?.quantity ?? null}
        onSave={async (body) => {
          if (!projectId || !quoteId || !draft || !supplyLine) return
          await setQuoteSupply(projectId, quoteId, draft.id, { ...body, lineIds: [supplyLine.id] })
          await reload()
        }}
      />

      <SelectSalesItemDialog
        open={isSelectItemOpen}
        onOpenChange={setIsSelectItemOpen}
        projectId={projectId}
        onSelectFamily={(family) => {
          setSelectedFamily(family)
          setIsSelectItemOpen(false)
          setIsFamilyEditorOpen(true)
        }}
      />

      <QuoteVersionBaseFinishesDialog
        open={isBaseFinishesOpen}
        onOpenChange={setIsBaseFinishesOpen}
        projectId={projectId}
        quoteId={selectedQuoteId}
        versionId={selectedDraftVersionId}
        options={projectOptions}
        onSaved={async () => {
          try {
            await reload()
          } catch {
            setError('Erreur lors du rafraîchissement du devis')
          }
        }}
      />

      <ImportedLineDialog
        open={isImportedLineOpen}
        onOpenChange={(next) => {
          setIsImportedLineOpen(next)
          if (!next) {
            setEditingLine(null)
            setEditingLineId(null)
            setImportedLineOrigin(null)
          }
        }}
        projectId={projectId}
        quoteId={selectedQuoteId}
        versionId={selectedDraftVersionId}
        lineId={editingLineId}
        initialLine={editingLine}
        origin={importedLineOrigin}
        showOriginFirst={showOriginFirst}
        onSubmitted={async () => {
          try {
            await reload()
          } catch {
            setError('Erreur lors du rafraîchissement du devis')
          }
        }}
      />

      <DiscountLineDialog
        open={isDiscountDialogOpen}
        onOpenChange={(next) => {
          setIsDiscountDialogOpen(next)
          if (!next) {
            setEditingLine(null)
            setEditingLineId(null)
            setIsEditingDiscount(false)
          }
        }}
        projectId={projectId}
        quoteId={selectedQuoteId}
        versionId={selectedDraftVersionId}
        mode={isEditingDiscount ? 'update' : 'create'}
        lineId={editingLineId}
        initialLine={editingLine}
        onSubmitted={async () => {
          try {
            await reload()
          } catch {
            setError('Erreur lors du rafraîchissement du devis')
          }
        }}
      />

      <NoteLineDialog
        open={isNoteDialogOpen}
        onOpenChange={(next) => {
          setIsNoteDialogOpen(next)
          if (!next) {
            setEditingLine(null)
            setEditingLineId(null)
            setIsEditingNote(false)
          }
        }}
        projectId={projectId}
        quoteId={selectedQuoteId}
        versionId={selectedDraftVersionId}
        mode={isEditingNote ? 'update' : 'create'}
        lineId={editingLineId}
        initialLine={editingLine}
        onSubmitted={async () => {
          try {
            await reload()
          } catch {
            setError('Erreur lors du rafraîchissement du devis')
          }
        }}
      />

      <FamilyEditorSidebar
        open={isFamilyEditorOpen}
        family={selectedFamily}
        projectId={projectId}
        quoteId={selectedQuoteId}
        versionId={selectedDraftVersionId}
        mode={isEditingFamilyLine ? 'update' : 'create'}
        lineId={editingLineId}
        initialLine={editingLine}
        onClose={() => {
          setIsFamilyEditorOpen(false)
          setSelectedFamily(null)
          setEditingLine(null)
          setEditingLineId(null)
          setIsEditingFamilyLine(false)
        }}
        onSubmitted={async () => {
          try {
            await reload()
          } catch {
            setError('Erreur lors du rafraîchissement du devis')
          } finally {
            setIsFamilyEditorOpen(false)
            setSelectedFamily(null)
            setEditingLine(null)
            setEditingLineId(null)
            setIsEditingFamilyLine(false)
          }
        }}
      />
    </div>
  )
}
